// Vista em lista do pipeline de licitações: ordena por prazo, valor, fit e
// etapa; a etapa se troca na linha; rodapé com o total do que está na tela.
import { Select, Table, Tag, Typography, Tooltip, Empty, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { WarningOutlined } from '@ant-design/icons';
import { ETAPAS, TERMINAIS, normalizar, rotuloDe, etapaDe, ehAberta } from './etapas';
import { proximoPrazo, prontidao, valorDaLicitacao, diasNaEtapa, rotuloEnvio, situacaoDoOrgao, dicaDoOrgao, type LicitacaoPipeline } from './saudeLicitacao';
import { AvatarDono } from '../../comercial-pipeline/pecas';
import { fmt } from '../../comercial-pipeline/utils';
import { fitTecnicoUI } from '../fitTecnico';
import { tituloDoObjeto } from '../tituloDoObjeto';

const { Text } = Typography;

const OPCOES_ETAPA = [...ETAPAS, ...TERMINAIS].map(e => ({ label: e.rotulo, value: e.codigo }));

export function ListaLicitacoes({ lista, loading, nomes, podeOperar, onAbrir, onMover }: {
  lista: LicitacaoPipeline[];
  loading: boolean;
  nomes: Record<string, string>;
  podeOperar: boolean;
  onAbrir: (l: LicitacaoPipeline) => void;
  onMover: (l: LicitacaoPipeline, para: string) => void;
}) {
  const chavePrazo = (l: LicitacaoPipeline) => {
    const p = proximoPrazo(l);
    if (!p) return '9999';
    return (p.classe.vencida ? '9998-' : '') + p.data;
  };
  const cols: ColumnsType<LicitacaoPipeline> = [
    {
      title: 'Edital / órgão', key: 'ed',
      sorter: (a, b) => String(a.orgao || '').localeCompare(String(b.orgao || ''), 'pt-BR'),
      render: (_, l) => (
        <div style={{ minWidth: 0 }}>
          <Button type="link" size="small" onClick={() => onAbrir(l)}
            style={{ padding: 0, height: 'auto', color: '#0930D0', fontWeight: 600, whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.3 }}>
            {l.numero_edital || 'Edital sem número'}
          </Button>
          <div style={{ fontSize: 11, color: '#5A6378' }}>
            {l.orgao || '—'}{l.uf ? ` · ${[l.cidade, l.uf].filter(Boolean).join('/')}` : ''}
          </div>
          <div style={{ fontSize: 11, color: '#3C4257', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }} title={l.objeto || ''}>
            {tituloDoObjeto(l.objeto) || l.objeto}
          </div>
        </div>
      ),
    },
    {
      title: 'Etapa', key: 'et', width: 190,
      sorter: (a, b) => [...ETAPAS, ...TERMINAIS].findIndex(e => e.codigo === normalizar(a.status_interno))
        - [...ETAPAS, ...TERMINAIS].findIndex(e => e.codigo === normalizar(b.status_interno)),
      render: (_, l) => {
        const e = etapaDe(l.status_interno);
        return podeOperar ? (
          <Select value={normalizar(l.status_interno)} size="small" variant="borderless" showSearch optionFilterProp="label"
            popupMatchSelectWidth={false} options={OPCOES_ETAPA} onChange={(v) => onMover(l, v)}
            style={{ width: '100%', background: e?.fundo, borderRadius: 4, fontWeight: 600 }}
            labelRender={(o) => <span style={{ color: e?.cor, fontWeight: 600, fontSize: 12 }}>{o.label}</span>} />
        ) : <Tag style={{ background: e?.fundo, color: e?.cor, border: 0 }}>{rotuloDe(l.status_interno)}</Tag>;
      },
    },
    {
      title: 'Próximo prazo', key: 'pz', width: 170,
      sorter: (a, b) => chavePrazo(a).localeCompare(chavePrazo(b)), defaultSortOrder: 'ascend',
      render: (_, l) => {
        const p = proximoPrazo(l);
        // Suspensa/revogada pelo órgão (07/10/2026): no lugar de "proposta vencida".
        const o = ehAberta(l.status_interno) ? situacaoDoOrgao(l) : null;
        const selo = o && (
          <Tooltip title={dicaDoOrgao(o, l.prazo_efetivo)}>
            <div style={{ color: o.cor, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
              {o.tipo === 'suspensa' ? '⛔ ' : '✖ '}{o.texto}
            </div>
          </Tooltip>
        );
        if (selo && !p) return selo;
        if (!p) {
          const envio = rotuloEnvio(l);
          return envio
            ? <span style={{ color: '#0a7f4a', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>✓ {envio}</span>
            : <Text type="secondary">—</Text>;
        }
        return (
          <>
            {selo}
            <Tooltip title={p.classe.data.toLocaleString('pt-BR')}>
              <span style={{ color: p.classe.cor, fontWeight: p.classe.bold ? 700 : 400, fontSize: 12, whiteSpace: 'nowrap' }}>
                {p.classe.icone}{p.rotulo} {p.classe.rotulo}
              </span>
            </Tooltip>
          </>
        );
      },
    },
    {
      title: 'Valor', key: 'va', align: 'right', width: 118,
      sorter: (a, b) => valorDaLicitacao(a) - valorDaLicitacao(b),
      render: (_, l) => valorDaLicitacao(l) > 0
        ? <Text strong style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmt(valorDaLicitacao(l))}</Text>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Fit técnico', dataIndex: 'fit_score', key: 'fit', align: 'right', width: 85,
      sorter: (a, b) => (a.fit_score ?? -1) - (b.fit_score ?? -1),
      render: (_, l) => { const fit = fitTecnicoUI(l); return <Tooltip title={fit.dica}><span style={{ fontWeight: 600, color: fit.color }}>{fit.label}</span></Tooltip>; },
    },
    {
      title: 'Responsável', dataIndex: 'responsavel_email', key: 'r', width: 170,
      sorter: (a, b) => String(a.responsavel_email || '').localeCompare(String(b.responsavel_email || '')),
      render: (v) => {
        const nome = nomes[String(v || '').toLowerCase()] || v;
        return (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
            <AvatarDono nome={nome || null} tamanho={20} />
            <Text ellipsis style={{ maxWidth: 120, fontSize: 12, color: v ? undefined : '#9AA0B4' }}>{nome || 'sem responsável'}</Text>
          </span>
        );
      },
    },
    {
      title: 'Alterações', key: 'al', width: 100, align: 'center',
      sorter: (a, b) => (Number(a.eventos_unack_count) || 0) - (Number(b.eventos_unack_count) || 0),
      render: (_, l) => (Number(l.eventos_unack_count) || 0) > 0
        ? <Tooltip title={l.ultimo_evento_resumo || ''}><span style={{ color: '#B91626', fontWeight: 600 }}><WarningOutlined /> {l.eventos_unack_count}</span></Tooltip>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Habilitação', key: 'hab', width: 100, align: 'center',
      render: (_, l) => { const p = prontidao(l); return p ? <span style={{ color: p.pronta ? '#0a7f4a' : '#5A6378' }}>{p.texto}</span> : <Text type="secondary">—</Text>; },
    },
    {
      title: 'Na etapa', key: 'ne', width: 90, align: 'right',
      sorter: (a, b) => (diasNaEtapa(a) ?? -1) - (diasNaEtapa(b) ?? -1),
      render: (_, l) => { const d = diasNaEtapa(l); return d == null ? <Text type="secondary">—</Text> : <Text style={{ fontSize: 12 }}>{d === 0 ? 'hoje' : `${d} d`}</Text>; },
    },
  ];

  const total = lista.reduce((a, l) => a + valorDaLicitacao(l), 0);
  return (
    <Table rowKey="id" columns={cols} dataSource={lista} loading={loading} size="small" sticky showSorterTooltip={false}
      pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} licitações` }}
      locale={{ emptyText: <Empty description="Nenhuma licitação com estes filtros." /> }}
      scroll={{ x: 1180 }}
      footer={() => (
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: '#5A6378' }}>
          <span><b style={{ color: '#0E1433' }}>{lista.length}</b> {lista.length === 1 ? 'licitação' : 'licitações'} na lista</span>
          <span>Valor estimado <b style={{ color: '#0E1433', fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</b></span>
        </div>
      )} />
  );
}
